import { useEffect, useState } from 'react';
import { STRINGS, type Lang, LANGS } from '../../i18n/strings';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { errorText, rpc, type AppError } from '../admin/api';
import { HeadFrame, type HeadHomeData } from './HeadPages';
import type { GradeWindow, Window } from './HeadInsights';

type Period = 'autumn' | 'spring' | 'year' | 'custom';
type Report = {
  school: { name: string; code: string };
  period: { kind: Period; from: string; to: string };
  grades: string[];
  whole_school: boolean;
  weeks: { from: string; to: string; school: Window; topics: Record<string, number | null> | null; grades: GradeWindow[] }[];
  summary: { weeks_shown: number; responses: number; mean: number } | null;
  participation: { label: string; sessions: number; responses: number }[];
  inactive_classes: number;
  context_tags: Record<string, number>;
  actions: Record<string, number>;
  replies: number;
  below_floor_sessions: number;
  settings: { period?: Period; grades?: string[]; lang?: Lang } | null;
};
type Section = 'participation' | 'wellbeing' | 'context' | 'actions' | 'notes';

// J1: the communal welfare evidence report. Built from complete weeks only;
// the head adds their own commentary per section, then prints to PDF or
// downloads a Word file. Each export is logged (never the numbers).
export function WelfareReportPage() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="report" title={t['h2.reportTitle']}>
      {(_ctx, home) => <ReportBody home={home} />}
    </HeadFrame>
  );
}

function ReportBody({ home }: { home: HeadHomeData }) {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const [period, setPeriod] = useState<Period>('autumn');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [grades, setGrades] = useState<string[]>([]);
  const [rlang, setRlang] = useState<Lang>(lang);
  const [report, setReport] = useState<Report | null>(null);
  const [comment, setComment] = useState<Record<Section, string>>({ participation: '', wellbeing: '', context: '', actions: '', notes: '' });
  const [err, setErr] = useState<AppError | null>(null);
  const [busy, setBusy] = useState(false);
  const allGrades = home.grades;

  useEffect(() => {
    // Start from the saved choices.
    rpc<Report>('head_welfare_report', { p_period: 'autumn' }).then((r) => {
      const s = r.data?.settings;
      if (s?.period) setPeriod(s.period);
      if (s?.grades) setGrades(s.grades);
      if (s?.lang) setRlang(s.lang);
    });
  }, []);

  async function build() {
    setBusy(true);
    setErr(null);
    const r = await rpc<Report>('head_welfare_report', {
      p_period: period,
      p_from: period === 'custom' ? from || null : null,
      p_to: period === 'custom' ? to || null : null,
      p_grade_ids: grades.length ? grades : null,
    });
    setBusy(false);
    if (r.error) {
      setErr(r.error);
      return;
    }
    setReport(r.data);
    announce(t['h2.reportReady']);
    rpc('head_save_report_settings', { p_settings: { period, grades, lang: rlang } });
  }

  async function exportAs(format: 'pdf' | 'word') {
    if (!report) return;
    const logged = await rpc('head_log_report_export', { p_period: report.period, p_grades: report.grades, p_format: format });
    if (logged.error) {
      setErr(logged.error);
      return;
    }
    if (format === 'pdf') {
      window.print();
    } else {
      const html = reportHtml(report, comment, rlang);
      const blob = new Blob([html], { type: 'application/msword' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `welfare-report-${report.school.code}-${report.period.from}.doc`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }
    announce(t['h2.exportLogged']);
  }

  return (
    <>
      <section className="panel p5 no-print" aria-labelledby="rep-setup">
        <h2 className="panel-title" id="rep-setup">{t['h2.reportSetup']}</h2>
        <p className="form-hint">{t['h2.reportIntro']}</p>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor="rep-period">{t['h2.period']}</label>
            <div className="select-wrap">
              <select id="rep-period" className="select" value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                {(['autumn', 'spring', 'year', 'custom'] as Period[]).map((p) => (
                  <option key={p} value={p}>{t[`h2.period.${p}` as 'h2.period.autumn']}</option>
                ))}
              </select>
            </div>
          </div>
          {period === 'custom' && (
            <>
              <div className="field-group">
                <label className="field-label" htmlFor="rep-from">{t['h2.from']}</label>
                <input id="rep-from" className="field" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="rep-to">{t['h2.to']}</label>
                <input id="rep-to" className="field" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </>
          )}
          <div className="field-group">
            <label className="field-label" htmlFor="rep-lang">{t['h2.reportLang']}</label>
            <div className="select-wrap">
              <select id="rep-lang" className="select" value={rlang} onChange={(e) => setRlang(e.target.value as Lang)}>
                {LANGS.map((l) => (
                  <option key={l.code} value={l.code}>{l.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {allGrades && allGrades.length > 0 && (
          <fieldset className="fieldset">
            <legend className="field-label">{t['h2.reportGrades']}</legend>
            <div className="chip-row">
              {allGrades.map((g) => {
                const on = grades.includes(g.id);
                return (
                  <label key={g.id} className={`chip-check${on ? ' on' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => setGrades((p) => (on ? p.filter((x) => x !== g.id) : [...p, g.id]))} />
                    {t['h2.grade'](g.label)}
                  </label>
                );
              })}
            </div>
            <p className="form-hint">{t['h2.reportGradesHint']}</p>
          </fieldset>
        )}
        {home.me.scope_grades && <p className="form-hint">{t['h2.scopeGrades'](home.me.scope_grades.join(', '))}</p>}
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={build} disabled={busy}>{busy ? t['pa.working'] : t['h2.preview']}</button>
          {report && (
            <>
              <button type="button" className="btn btn-ghost" onClick={() => exportAs('pdf')}>{t['h2.exportPdf']}</button>
              <button type="button" className="btn btn-ghost" onClick={() => exportAs('word')}>{t['h2.exportWord']}</button>
            </>
          )}
        </div>
        {report && <p className="form-hint">{t['h2.exportHint']}</p>}
      </section>

      {report && <ReportView report={report} comment={comment} setComment={setComment} rlang={rlang} />}
    </>
  );
}

function fmtDate(lang: Lang, d: string) {
  const locale = lang === 'en' ? 'en-GB' : lang === 'fi' ? 'fi-FI' : 'sv-FI';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', year: 'numeric' }).format(new Date(d));
}

function winText(R: (typeof STRINGS)[Lang], w: Window | null, lang: Lang): string {
  if (!w) return '–';
  if (w.status === 'ok') {
    const m = lang === 'en' ? w.mean.toFixed(1) : w.mean.toFixed(1).replace('.', ',');
    return `${m}/5 (${R['h2.n'](w.total)})`;
  }
  if (w.total === 0) return R['h2.noAnswers'];
  return R[`h2.st.${w.status}` as 'h2.st.insufficient'];
}

function ReportView({ report, comment, setComment, rlang }: {
  report: Report; comment: Record<Section, string>; setComment: (c: Record<Section, string>) => void; rlang: Lang;
}) {
  const R = STRINGS[rlang];
  const { t } = useI18n();
  const gradeLabels = report.weeks[0]?.grades.map((g) => g.label) ?? [];
  const commentBox = (s: Section) => (
    <div className="field-group report-comment">
      <label className="field-label no-print" htmlFor={`c-${s}`}>{t['h2.commentLabel']}</label>
      <textarea id={`c-${s}`} className="field no-print" rows={2} maxLength={2000} value={comment[s]}
        onChange={(e) => setComment({ ...comment, [s]: e.target.value })} />
      {comment[s] && <p className="report-own"><strong>{R['h2.headComment']}:</strong> {comment[s]}</p>}
    </div>
  );
  return (
    <article className="panel p5 report" lang={rlang} aria-labelledby="rep-h">
      <h2 className="panel-title" id="rep-h">{R['h2.reportDocTitle']}</h2>
      <p>
        <strong>{report.school.name}</strong> · {report.school.code} · {fmtDate(rlang, report.period.from)}–{fmtDate(rlang, report.period.to)}
        {' · '}{report.whole_school ? R['h2.wholeSchool'] : R['h2.gradesLabel'](report.grades.join(', '))}
      </p>
      <p className="form-hint">{R['h2.reportBasis']}</p>

      <h3 className="overline">{R['h2.secParticipation']}</h3>
      <dl className="kv">
        {report.participation.map((p) => (
          <div key={p.label}><dt>{R['h2.grade'](p.label)}</dt><dd>{R['h2.partLine'](p.sessions, p.responses)}</dd></div>
        ))}
        <div><dt>{R['h2.inactive']}</dt><dd>{report.inactive_classes}</dd></div>
      </dl>
      {commentBox('participation')}

      <h3 className="overline">{R['h2.secWellbeing']}</h3>
      {report.summary ? (
        <p>{R['h2.summaryLine'](report.summary.weeks_shown, report.weeks.length, report.summary.responses,
             rlang === 'en' ? report.summary.mean.toFixed(1) : report.summary.mean.toFixed(1).replace('.', ','))}</p>
      ) : (
        <p>{R['h2.summaryNone']}</p>
      )}
      <div className="table-wrap" tabIndex={0} role="region" aria-label={R['h2.secWellbeing']}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{R['h2.week']}</th>
              <th scope="col">{report.whole_school ? R['h2.school'] : R['h2.selected']}</th>
              {gradeLabels.map((g) => <th key={g} scope="col">{R['h2.grade'](g)}</th>)}
            </tr>
          </thead>
          <tbody>
            {report.weeks.map((w) => (
              <tr key={w.from}>
                <th scope="row">{fmtDate(rlang, w.from)}</th>
                <td>{winText(R, w.school, rlang)}</td>
                {w.grades.map((g) => <td key={g.grade_id}>{winText(R, g, rlang)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {commentBox('wellbeing')}

      <h3 className="overline">{R['h2.secContext']}</h3>
      <p>{Object.entries(report.context_tags).map(([k, n]) => `${R[`ts.tag.${k}` as 'ts.tag.normal']}: ${n}`).join(' · ') || R['h2.nothing']}</p>
      {commentBox('context')}

      <h3 className="overline">{R['h2.secActions']}</h3>
      <p>{Object.entries(report.actions).map(([k, n]) => `${R[`act.${k}` as 'act.other']}: ${n}`).join(' · ') || R['h2.nothing']}</p>
      <p>{R['h2.repliesLine'](report.replies)}</p>
      {commentBox('actions')}

      <h3 className="overline">{R['h2.secNotes']}</h3>
      <p>{R['h2.notesLine'](report.below_floor_sessions)}</p>
      <p className="form-hint">{R['h2.never']}</p>
      {commentBox('notes')}
      <p className="no-print form-hint">{t['h2.previewOnly']}</p>
    </article>
  );
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

// The Word download: a plain HTML document Word opens as .doc. Every value
// is escaped; the head's commentary is marked as theirs.
function reportHtml(r: Report, c: Record<Section, string>, lang: Lang): string {
  const R = STRINGS[lang];
  const grades = r.weeks[0]?.grades.map((g) => g.label) ?? [];
  const own = (s: Section) => (c[s] ? `<p><b>${esc(R['h2.headComment'])}:</b> ${esc(c[s])}</p>` : '');
  const rows = r.weeks.map((w) => `<tr><td>${esc(fmtDate(lang, w.from))}</td><td>${esc(winText(R, w.school, lang))}</td>${w.grades.map((g) => `<td>${esc(winText(R, g, lang))}</td>`).join('')}</tr>`).join('');
  return `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><title>${esc(R['h2.reportDocTitle'])}</title>
<style>body{font-family:Calibri,Arial,sans-serif;font-size:11pt}table{border-collapse:collapse}td,th{border:1px solid #999;padding:4px 8px;text-align:left}h1{font-size:16pt}h2{font-size:13pt}</style></head><body>
<h1>${esc(R['h2.reportDocTitle'])}</h1>
<p><b>${esc(r.school.name)}</b> · ${esc(r.school.code)} · ${esc(fmtDate(lang, r.period.from))}–${esc(fmtDate(lang, r.period.to))} · ${esc(r.whole_school ? R['h2.wholeSchool'] : R['h2.gradesLabel'](r.grades.join(', ')))}</p>
<p><i>${esc(R['h2.reportBasis'])}</i></p>
<h2>${esc(R['h2.secParticipation'])}</h2><ul>${r.participation.map((p) => `<li>${esc(R['h2.grade'](p.label))}: ${esc(R['h2.partLine'](p.sessions, p.responses))}</li>`).join('')}<li>${esc(R['h2.inactive'])}: ${r.inactive_classes}</li></ul>${own('participation')}
<h2>${esc(R['h2.secWellbeing'])}</h2><p>${esc(r.summary ? R['h2.summaryLine'](r.summary.weeks_shown, r.weeks.length, r.summary.responses, lang === 'en' ? r.summary.mean.toFixed(1) : r.summary.mean.toFixed(1).replace('.', ',')) : R['h2.summaryNone'])}</p>
<table><tr><th>${esc(R['h2.week'])}</th><th>${esc(r.whole_school ? R['h2.school'] : R['h2.selected'])}</th>${grades.map((g) => `<th>${esc(R['h2.grade'](g))}</th>`).join('')}</tr>${rows}</table>${own('wellbeing')}
<h2>${esc(R['h2.secContext'])}</h2><p>${esc(Object.entries(r.context_tags).map(([k, n]) => `${R[`ts.tag.${k}` as 'ts.tag.normal']}: ${n}`).join(' · ') || R['h2.nothing'])}</p>${own('context')}
<h2>${esc(R['h2.secActions'])}</h2><p>${esc(Object.entries(r.actions).map(([k, n]) => `${R[`act.${k}` as 'act.other']}: ${n}`).join(' · ') || R['h2.nothing'])}</p><p>${esc(R['h2.repliesLine'](r.replies))}</p>${own('actions')}
<h2>${esc(R['h2.secNotes'])}</h2><p>${esc(R['h2.notesLine'](r.below_floor_sessions))}</p><p>${esc(R['h2.never'])}</p>${own('notes')}
</body></html>`;
}
